import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Trophy, Sparkles, Clock, Star, ArrowRight, ArrowLeft, CheckCircle2, X, Loader2, Medal, Crown } from 'lucide-react';

const STAR_COLOR = '#F59E0B';

/**
 * MathOlympiadPanel — Student-facing modal for the weekly Math Olympiad.
 * Flows:
 *   1. Active challenge → question-by-question attempt UI → submit
 *   2. Already attempted → result summary + leaderboard
 *   3. No active challenge → empty state
 */
export const MathOlympiadPanel = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [pastAttempt, setPastAttempt] = useState(null);
  const [answers, setAnswers] = useState({}); // { 0: 2, 1: 0 }
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [view, setView] = useState('intro'); // intro | quiz | result | leaderboard
  const [startedAt, setStartedAt] = useState(null);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/math-olympiad/active');
      setChallenge(res.data?.challenge || null);
      setPastAttempt(res.data?.user_attempt || null);
      if (res.data?.user_attempt && res.data?.challenge) {
        // Already attempted — auto-show results + leaderboard
        setResult({ submission: res.data.user_attempt, feedback: '' });
        await loadLeaderboard(res.data.challenge.id);
        setView('result');
      } else {
        setView('intro');
      }
    } catch {
      setChallenge(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeaderboard = async (chId) => {
    try {
      const res = await api.get(`/math-olympiad/${chId}/leaderboard`);
      setLeaderboard(res.data);
    } catch { setLeaderboard(null); }
  };

  useEffect(() => {
    if (!open) return;
    loadActive();
    setAnswers({});
    setCurrentIdx(0);
    setResult(null);
    setLeaderboard(null);
  }, [open, loadActive]);

  const startAttempt = () => {
    setStartedAt(Date.now());
    setView('quiz');
  };

  const selectAnswer = (qi, oi) => {
    setAnswers((prev) => ({ ...prev, [qi]: oi }));
  };

  const submit = async () => {
    if (!challenge) return;
    setSubmitting(true);
    try {
      const duration_seconds = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;
      const res = await api.post(`/math-olympiad/${challenge.id}/attempt`, { answers, duration_seconds });
      setResult(res.data);
      toast.success(res.data?.feedback || 'Submitted!');
      await loadLeaderboard(challenge.id);
      setView('result');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (n) => (
    <div className="flex gap-0.5">
      {[1, 2, 3].map((s) => (
        <Star
          key={s}
          className="w-4 h-4"
          fill={s <= (n || 0) ? STAR_COLOR : 'none'}
          stroke={STAR_COLOR}
          strokeWidth={2}
        />
      ))}
    </div>
  );

  const totalQs = challenge?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;
  const currentQ = challenge?.questions?.[currentIdx];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden border-0" data-testid="math-olympiad-dialog">
        {/* Header */}
        <div
          className="px-5 py-4 flex items-center justify-between text-white"
          style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
        >
          <DialogHeader className="text-left p-0 m-0">
            <DialogTitle className="flex items-center gap-2 text-white text-base font-extrabold">
              <Trophy className="w-5 h-5" />
              Math Olympiad
              {challenge && <span className="ml-2 text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">{challenge.week_iso}</span>}
            </DialogTitle>
            {challenge && <p className="text-xs opacity-90 mt-0.5">{challenge.title} · Theme: {challenge.theme}</p>}
          </DialogHeader>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/15 h-8 px-2" data-testid="olympiad-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="bg-slate-50 px-5 py-5" style={{ minHeight: '420px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-64 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : !challenge ? (
            <div className="flex flex-col items-center justify-center h-64 text-center text-slate-500">
              <Sparkles className="w-10 h-10 mb-2 text-amber-400" />
              <p className="text-sm font-bold text-[#0F172A]">No active Olympiad challenge right now.</p>
              <p className="text-xs mt-1">A new one drops every week — come back soon!</p>
            </div>
          ) : view === 'intro' ? (
            <div className="text-center max-w-md mx-auto py-6" data-testid="olympiad-intro">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FEF3C7, #FED7AA)' }}>
                <Trophy className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-extrabold text-[#0F172A]">{challenge.title}</h3>
              <p className="text-sm text-[#334155] font-medium mt-1">{challenge.theme} · Grade band: {challenge.grade_band}</p>
              <div className="flex justify-center gap-4 mt-4 text-xs text-[#64748B] font-bold">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {Math.round((challenge.duration_seconds || 600) / 60)} min</span>
                <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {totalQs} questions</span>
              </div>
              <Button
                onClick={startAttempt}
                className="mt-6 px-8 h-11 rounded-full text-sm font-extrabold text-white"
                style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
                data-testid="olympiad-start"
              >
                Start Challenge <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <p className="text-[11px] text-[#64748B] mt-3">You can attempt this challenge only once. Be sharp!</p>
            </div>
          ) : view === 'quiz' && currentQ ? (
            <div data-testid="olympiad-quiz">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-extrabold text-[#64748B] uppercase tracking-wider">
                  Q{currentIdx + 1} / {totalQs}
                </span>
                <span className="text-xs font-bold text-[#10B981]">
                  {answeredCount}/{totalQs} answered
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-amber-500" style={{ width: `${(answeredCount / totalQs) * 100}%` }} />
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-base font-bold text-[#0F172A] mb-4">{currentQ.prompt}</p>
                <div className="space-y-2">
                  {currentQ.options.map((opt, oi) => {
                    const chosen = answers[currentIdx] === oi;
                    return (
                      <button
                        key={oi}
                        onClick={() => selectAnswer(currentIdx, oi)}
                        className={`w-full text-left rounded-lg border-2 px-4 py-2.5 text-sm font-semibold transition-all ${
                          chosen
                            ? 'border-amber-500 bg-amber-50 text-amber-900'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-amber-300'
                        }`}
                        data-testid={`olympiad-option-${currentIdx}-${oi}`}
                      >
                        <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center mr-2 text-xs font-extrabold ${chosen ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-between items-center mt-5">
                <Button
                  variant="outline"
                  onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                  disabled={currentIdx === 0}
                  className="rounded-full"
                  data-testid="olympiad-prev"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                {currentIdx < totalQs - 1 ? (
                  <Button
                    onClick={() => setCurrentIdx((i) => i + 1)}
                    className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="olympiad-next"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={submitting || answeredCount !== totalQs}
                    className="rounded-full text-white font-extrabold"
                    style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
                    data-testid="olympiad-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit <CheckCircle2 className="w-4 h-4 ml-1" /></>}
                  </Button>
                )}
              </div>
            </div>
          ) : view === 'result' ? (
            <div className="space-y-4" data-testid="olympiad-result">
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <Crown className="w-10 h-10 mx-auto text-amber-500 mb-1" />
                <p className="text-3xl font-extrabold text-[#0F172A]">{result?.submission?.score_percent ?? 0}%</p>
                <p className="text-sm font-bold text-[#334155] mt-0.5">
                  {result?.submission?.score ?? 0} / {result?.submission?.total_questions ?? 0} correct
                </p>
                <div className="flex justify-center my-2">{renderStars(result?.submission?.stars)}</div>
                {result?.feedback && <p className="text-sm font-semibold text-amber-700 mt-1">{result.feedback}</p>}
              </div>

              {/* Leaderboard inline */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-sm font-extrabold text-[#0F172A] flex items-center gap-1.5">
                    <Medal className="w-4 h-4 text-amber-500" /> Leaderboard
                  </span>
                  <span className="text-xs font-bold text-[#64748B]">{leaderboard?.total_attempts || 0} attempts</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  {(leaderboard?.leaderboard || []).length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-6">Be the first to attempt!</p>
                  ) : (
                    leaderboard.leaderboard.map((row) => (
                      <div
                        key={`${row.rank}-${row.student_id}`}
                        className={`flex items-center justify-between px-4 py-2.5 text-sm ${row.is_you ? 'bg-amber-50' : ''}`}
                        data-testid={`olympiad-row-${row.rank}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold ${row.rank === 1 ? 'bg-amber-500 text-white' : row.rank === 2 ? 'bg-slate-400 text-white' : row.rank === 3 ? 'bg-orange-400 text-white' : 'bg-slate-100 text-slate-600'}`}>
                            {row.rank}
                          </span>
                          <span className="font-bold text-[#0F172A]">{row.student_name}{row.is_you ? ' (you)' : ''}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {renderStars(row.stars)}
                          <span className="text-xs font-extrabold text-[#0F172A] w-10 text-right">{row.score_percent}%</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MathOlympiadPanel;
